import AgentChat from '@/components/AgentChat';
import ScrollRestoration from '@/components/ScrollRestoration';
import SpinnerFullPage from '@/components/SpinnerFullPage';
import StenoAiChatbot from '@/components/StenoAiChatbot';
import AnimatePlanPagesProvider from '@/contexts/AnimatedPlanPagesProvider';
import FeatureFlagProvider from '@/contexts/FeatureFlagProvider';
import useIsCompanyUser from '@/hooks/useIsCompanyUser';
import { queryClient } from '@/services/graphql/queryClient';
import GlobalContainer from '@/theme/Global';
import '@/utils/fix-date';
import rem from '@/utils/rem';
import { Box, ChakraProvider, Flex, Icon, Text, VStack, createStandaloneToast } from '@chakra-ui/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useRef, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { FaRobot } from 'react-icons/fa';
import { BrowserRouter, useSearchParams } from 'react-router-dom';

import AppRoutes from './AppRoutes';
import './sentry.config';
import { restoreSession } from './services/auth/oauthService';
import { useOAuthCallback } from './services/user/hooks';
import { useActionModal } from './stores/useActionModal';
import theme from './theme';
import { initTracking } from './utils/tracking';

const { ToastContainer } = createStandaloneToast({ theme });

const MaintenanceMode = () => {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      padding={rem(16)}
      backgroundColor="background-primary"
    >
      <Box
        width="full"
        maxWidth={rem(500)}
        padding={rem(32)}
        borderRadius={rem(16)}
        boxShadow="lg"
        backgroundColor="background-tertiary"
      >
        <VStack align="center" textAlign="center" spacing={rem(20)}>
          <Icon as={FaRobot} boxSize={rem(80)} color="orange.400" />
          <Text color="text-primary" fontFamily="Satoshi" fontSize={rem(28)} fontWeight="bold">
            Under Maintenance
          </Text>
          <Text color="text-secondary" fontFamily="Satoshi" fontSize={rem(16)} lineHeight="1.6">
            We're currently performing scheduled maintenance to improve your experience. Our goal is to be back online
            within an hour. Thank you for your patience!
          </Text>
          <Text marginTop={rem(8)} color="text-secondary" fontFamily="Satoshi" fontSize={rem(14)} fontWeight="medium">
            The RPM Team
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};

// Component that uses router hooks - must be inside BrowserRouter
function AppContent() {
  const { opened: isActionModalOpen, setActionModalOpen } = useActionModal();
  const handleCallback = useOAuthCallback();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasOAuthCode = searchParams.has('code');
  const [isProcessingCallback, setIsProcessingCallback] = useState(hasOAuthCode);
  const [isRestoringSession, setIsRestoringSession] = useState(!hasOAuthCode);
  const hasProcessedCallback = useRef(false);

  useEffect(() => {
    const handlePopState = () => {
      if (isActionModalOpen) {
        setActionModalOpen(!isActionModalOpen);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isActionModalOpen, setActionModalOpen]);

  // Check for OAuth callback parameters on component mount
  useEffect(() => {
    async function processCallback() {
      const code = searchParams.get('code');
      if (code && !hasProcessedCallback.current) {
        hasProcessedCallback.current = true;
        setIsProcessingCallback(true);
        try {
          await handleCallback(code);
          searchParams.delete('code');
          searchParams.delete('state');
          setSearchParams(searchParams, { replace: true });
        } catch (error) {
          console.error('Error processing OAuth callback:', error);
          hasProcessedCallback.current = false;
        } finally {
          setIsProcessingCallback(false);
        }
      }
    }
    processCallback();
  }, [handleCallback, searchParams, setSearchParams]);

  // Restore session from stored refresh token on app startup (page reload, tab restore).
  // Skipped during OAuth callback since handleOAuthCallback will set the token.
  useEffect(() => {
    if (hasOAuthCode) return;
    restoreSession().finally(() => setIsRestoringSession(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Agent access gate: only @tonyrobbins.com emails for MVP
  const { isCompanyUser } = useIsCompanyUser();
  const isAgentEligible = import.meta.env.VITE_AGENT_ENABLED === 'true' && isCompanyUser;

  if (isProcessingCallback || isRestoringSession) {
    return <SpinnerFullPage />;
  }

  return (
    <>
      <StenoAiChatbot />
      {isAgentEligible && <AgentChat />}
      <AnimatePlanPagesProvider>
        <ScrollRestoration>
          <AppRoutes />
        </ScrollRestoration>
      </AnimatePlanPagesProvider>
    </>
  );
}

function App() {
  // amplitude tracking
  useEffect(() => initTracking(), []);

  const isMaintenanceMode = false; // TODO: Set to true to enable maintenance mode

  if (isMaintenanceMode) {
    return (
      <ChakraProvider resetCSS theme={theme}>
        <GlobalContainer />
        <QueryClientProvider client={queryClient}>
          <MaintenanceMode />
        </QueryClientProvider>
      </ChakraProvider>
    );
  }

  return (
    <HelmetProvider>
      <ChakraProvider resetCSS theme={theme}>
        {/* Uncomment to force dark mode even if user had saved light mode preference in localStorage */}
        {/* TODO: light mode is buggy and unfinished but is enabled temproraily by the request of the client */}
        {/* <DarkMode> */}
        <QueryClientProvider client={queryClient}>
          <FeatureFlagProvider environment={import.meta.env.VITE_ENV}>
            <GlobalContainer />
            <ToastContainer />
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>

            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />}

            {/* {(import.meta.env.VITE_ENV === 'uat' || import.meta.env.VITE_ENV === 'staging') && <EnvRibbon />} */}
          </FeatureFlagProvider>
        </QueryClientProvider>
        {/* </DarkMode> */}
      </ChakraProvider>
    </HelmetProvider>
  );
}

export default App;
